import React, { ComponentType } from 'react';
import { MemoExoticComponent, RefObject } from 'react';
import { TextBlock, TextBlockVariation } from './TextBlock';
import { ImageBlock, ImageBlockVariation } from './ImageBlock';
import { MultimediaBlock, MultimediaBlockVariation } from './MultimediaBlock';
import { DividerBlock, DividerBlockVariation } from './DividerBlock';
import { StatementBlock, StatementBlockVariation } from './StatementBlock';
import { ListBlock, ListBlockVariation } from './ListBlock';
import { QuoteBlock, QuoteBlockVariation } from './QuoteBlock';
import { GalleryBlock, GalleryBlockVariation } from './GalleryBlock';
import { ChartBlock, ChartBlockVariation } from './ChartBlock';
import { EmbedBlock, EmbedBlockVariation } from './EmbedBlock';
import { DrillBlockVariation } from './DrillBlock';
import { ScenarioDrillBlock, ScenarioDrillBlockVariation } from './ScenarioDrillBlock';
import { AccordionBlock, AccordionBlockVariation } from './AccordionBlock';
import { TabsBlock, TabsBlockVariation } from './TabsBlock';
import { ProcessBlock, ProcessBlockVariation } from './ProcessBlock';
import { PolicyBlock, PolicyBlockVariation } from './PolicyBlock';
import { ProcedureBlock, ProcedureBlockVariation } from './ProcedureBlock';
import { JobDescriptionBlock, JobDescriptionBlockVariation } from './JobDescriptionBlock';
import { FlowChartBlockVariation, FlowChartBlock } from './FlowChartBlock';
import { OrgChartBlockVariation, OrgChartBlock } from './OrgChartBlock';
import { GlossaryBlockVariation, GlossaryBlock } from './GlossaryBlock';
import { UserCourseStepResponse } from '@business/entities/user_course';
import { SequenceDrillBlockVariation, SequenceDrillBlock } from './SequenceDrillBlock';
import { StatementDrillBlock, StatementDrillBlockVariation } from './StatementDrillBlock';
import { EssayDrillBlock, EssayDrillBlockVariation } from './EssayDrillBlock';
import { FlashCardBlock } from './FlashCardBlock';
import { ListMatchingBlock } from './ListMatchingBlock';
import { TestBlockVariation } from './TestBlock/TestBlock';
import { MultipleChoiceTestBlock } from './MultipleChoiceTestBlock';
import { TodoListBlock, TodoListBlockVariation } from './TodoListBlock';
import { BlockTemplateBlock, BlockTemplateBlockVariation } from './BlockTemplateBlock';
import { LibraryCustomBlock, LibraryCustomBlockVariation } from './LibraryCustomBlock';

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace CourseBuilder {
  export enum BlockType {
    text = 'text',
    image = 'image',
    multimedia = 'multimedia',
    divider = 'divider',
    statement = 'statement',
    list = 'list',
    quote = 'quote',
    gallery = 'gallery',
    chart = 'chart',
    embed = 'embed',
    todoList = 'todoList',
    scenarioDrill = 'scenarioDrill',
    sequenceDrill = 'sequenceDrill',
    statementDrill = 'statementDrill',
    essayDrill = 'essayDrill',
    // flashCards = 'flashCards',
    policy = 'policy',
    procedure = 'procedure',
    jobDescription = 'jobDescription',
    flowChart = 'flowChart',
    orgChart = 'orgChart',
    glossary = 'glossary',
    accordion = 'accordion',
    tabs = 'tabs',
    process = 'process',
    flashCard = 'flashCard',
    listMatching = 'listMatching',
    multipleChoiceTest = 'multipleChoiceTest',
    blockTemplate = 'blockTemplate',
    libraryCustom = 'libraryCustom',
  }

  export enum BlockCategory {
    text = 'Text',
    image = 'Image',
    list = 'List',
    multimedia = 'Multimedia',
    divider = 'Divider',
    statement = 'Statement',
    quote = 'Quote',
    gallery = 'Gallery',
    chart = 'Chart',
    library = 'Library',
    todoList = 'Interactive',
    drills = 'Drills / Test',
    special = 'Special',
    test = 'Test',
    blockTemplate = 'block Template',
  }

  export const CategoryIcon: Record<BlockCategory, string> = {
    [BlockCategory.text]: 'fa fa-font',
    [BlockCategory.image]: 'fa fa-image',
    [BlockCategory.list]: 'fa fa-list',
    [BlockCategory.multimedia]: 'fa fa-video',
    [BlockCategory.divider]: 'fa fa-minus',
    [BlockCategory.statement]: 'fa fa-paragraph',
    [BlockCategory.quote]: 'fa fa-quote-left',
    [BlockCategory.gallery]: 'fa fa-images',
    [BlockCategory.chart]: 'fa fa-chart-line',
    [BlockCategory.library]: 'bx bx-file',
    [BlockCategory.todoList]: 'bx bx-file',
    [BlockCategory.drills]: 'bx bx-file',
    [BlockCategory.special]: 'bx bx-file',
    [BlockCategory.test]: 'bx bx-file',
    [BlockCategory.blockTemplate]: 'fa fa-cube',
  };

  export interface BlockHandle {
    openSettings: () => void;
  }

  export interface BlockProps<V> {
    id: string;
    handlesRef?: RefObject<BlockHandle>;
    variation: V;
    editable?: boolean;
    preview?: boolean;
    lessonId?: string;
    step?: UserCourseStepResponse;
  }

  export interface BlockConfig<V = any, C = MemoExoticComponent<ComponentType<BlockProps<V>>>> {
    type: BlockType;
    category: BlockCategory;
    component: C;
    variations: V[];
  }

  export const BLOCK_CONFIGS: Record<BlockType, BlockConfig> = {
    text: {
      type: BlockType.text,
      category: BlockCategory.text,
      component: TextBlock,
      variations: [
        TextBlockVariation.heading,
        TextBlockVariation.paragraphWithHeading,
        TextBlockVariation.paragraphWithSubheading,
        TextBlockVariation.paragraph,
        TextBlockVariation.subheading,
        TextBlockVariation.twoColumn,
        TextBlockVariation.threeColumn,
        TextBlockVariation.fourColumn,
      ],
    },
    image: {
      type: BlockType.image,
      category: BlockCategory.image,
      component: ImageBlock,
      variations: [
        ImageBlockVariation.imageCentered,
        ImageBlockVariation.imageFullWidth,
        ImageBlockVariation.imageWithText,
        ImageBlockVariation.textOnImage,
      ],
    },
    multimedia: {
      type: BlockType.multimedia,
      category: BlockCategory.multimedia,
      component: MultimediaBlock,
      variations: [MultimediaBlockVariation.video],
    },
    divider: {
      type: BlockType.divider,
      category: BlockCategory.divider,
      component: DividerBlock,
      variations: [DividerBlockVariation.divider, DividerBlockVariation.numbersDivider],
    },
    statement: {
      type: BlockType.statement,
      category: BlockCategory.statement,
      component: StatementBlock,
      variations: [
        StatementBlockVariation.statementA,
        StatementBlockVariation.statementB,
        StatementBlockVariation.statementC,
        StatementBlockVariation.statementD,
        StatementBlockVariation.note,
      ],
    },
    list: {
      type: BlockType.list,
      category: BlockCategory.list,
      component: ListBlock,
      variations: [ListBlockVariation.numberedList, ListBlockVariation.bulletedList],
    },
    quote: {
      type: BlockType.quote,
      category: BlockCategory.quote,
      component: QuoteBlock,
      variations: [
        QuoteBlockVariation.quoteA,
        QuoteBlockVariation.quoteB,
        QuoteBlockVariation.quoteC,
        QuoteBlockVariation.quoteD,
        QuoteBlockVariation.quoteOnImage,
      ],
    },
    gallery: {
      type: BlockType.gallery,
      category: BlockCategory.gallery,
      component: GalleryBlock,
      variations: [
        GalleryBlockVariation.carousel,
        GalleryBlockVariation.twoColumnGrid,
        GalleryBlockVariation.threeColumnGrid,
        GalleryBlockVariation.fourColumnGrid,
      ],
    },
    chart: {
      type: BlockType.chart,
      category: BlockCategory.chart,
      component: ChartBlock,
      variations: [ChartBlockVariation.lineChart, ChartBlockVariation.pieChart, ChartBlockVariation.barChart],
    },
    embed: {
      type: BlockType.embed,
      category: BlockCategory.multimedia,
      component: EmbedBlock,
      variations: [EmbedBlockVariation.embed],
    },

    policy: {
      type: BlockType.policy,
      category: BlockCategory.library,
      component: PolicyBlock,
      variations: [PolicyBlockVariation.policy],
    },
    procedure: {
      type: BlockType.procedure,
      category: BlockCategory.library,
      component: ProcedureBlock,
      variations: [ProcedureBlockVariation.procedure],
    },
    jobDescription: {
      type: BlockType.jobDescription,
      category: BlockCategory.library,
      component: JobDescriptionBlock,
      variations: [JobDescriptionBlockVariation.jobDescription],
    },
    libraryCustom: {
      type: BlockType.libraryCustom,
      category: BlockCategory.library,
      component: LibraryCustomBlock,
      variations: [LibraryCustomBlockVariation.libraryCustom],
    },
    flowChart: {
      type: BlockType.flowChart,
      category: BlockCategory.library,
      component: FlowChartBlock,
      variations: [FlowChartBlockVariation.flowChart],
    },
    orgChart: {
      type: BlockType.orgChart,
      category: BlockCategory.library,
      component: OrgChartBlock,
      variations: [OrgChartBlockVariation.orgChart],
    },
    glossary: {
      type: BlockType.glossary,
      category: BlockCategory.library,
      component: GlossaryBlock,
      variations: [GlossaryBlockVariation.glossary],
    },
    todoList: {
      type: BlockType.todoList,
      category: BlockCategory.todoList,
      component: TodoListBlock,
      variations: [TodoListBlockVariation.todoList],
    },
    scenarioDrill: {
      type: BlockType.scenarioDrill,
      category: BlockCategory.drills,
      component: ScenarioDrillBlock,
      variations: [ScenarioDrillBlockVariation.scenarioDrill],
    },
    accordion: {
      type: BlockType.accordion,
      category: BlockCategory.todoList,
      component: AccordionBlock,
      variations: [AccordionBlockVariation.accordion],
    },
    tabs: {
      type: BlockType.tabs,
      category: BlockCategory.todoList,
      component: TabsBlock,
      variations: [TabsBlockVariation.tabs],
    },
    process: {
      type: BlockType.process,
      category: BlockCategory.todoList,
      component: ProcessBlock,
      variations: [ProcessBlockVariation.process],
    },
    sequenceDrill: {
      type: BlockType.sequenceDrill,
      category: BlockCategory.drills,
      component: SequenceDrillBlock,
      variations: [SequenceDrillBlockVariation.sequenceDrill],
    },
    statementDrill: {
      type: BlockType.statementDrill,
      category: BlockCategory.drills,
      component: StatementDrillBlock,
      variations: [StatementDrillBlockVariation.statementDrill],
    },
    essayDrill: {
      type: BlockType.essayDrill,
      category: BlockCategory.drills,
      component: EssayDrillBlock,
      variations: [EssayDrillBlockVariation.essayDrill],
    },
    flashCard: {
      type: BlockType.flashCard,
      category: BlockCategory.drills,
      component: FlashCardBlock,
      variations: [DrillBlockVariation.flashCard],
    },
    listMatching: {
      type: BlockType.listMatching,
      category: BlockCategory.drills,
      component: ListMatchingBlock,
      variations: [TestBlockVariation.listMatching],
    },
    multipleChoiceTest: {
      type: BlockType.multipleChoiceTest,
      category: BlockCategory.drills,
      component: MultipleChoiceTestBlock,
      variations: [TestBlockVariation.multipleChoiceTest],
    },
    blockTemplate: {
      type: BlockType.blockTemplate,
      category: BlockCategory.blockTemplate,
      component: BlockTemplateBlock,
      variations: [BlockTemplateBlockVariation.blockTemplate],
    },
  };
}
